import { useEffect, useState } from 'react'
import { Navigate, Outlet, useLocation } from 'react-router-dom'
import { useStore } from '../stores/useStore'
import api from '../services/api'

/**
 * Componente para proteger rutas que requieren autenticacion
 * Redirige a /login si no hay token valido
 */
export function ProtectedRoute({ children, requireAdmin = false }) {
  const { token, user, setUser } = useStore()
  const [hydratingUser, setHydratingUser] = useState(false)
  const [combatChecked, setCombatChecked] = useState(false)
  const [combatCheckPath, setCombatCheckPath] = useState('')
  const [activeCombat, setActiveCombat] = useState(null)
  const location = useLocation()
  const isAuthenticated = !!token
  const isCombatCheckCurrent = combatChecked && combatCheckPath === location.pathname
  const hasCombatNavigationState = location.pathname === '/combat' && Boolean(location.state?.combatStarted)

  useEffect(() => {
    let mounted = true
    async function hydrateUser() {
      if (!token || user) return
      setHydratingUser(true)
      try {
        const res = await api.get('/users/me')
        const u = res.data?.user
        if (mounted && u) {
          setUser({
            ...u,
            isAdmin: Number(u.is_admin) === 1
          })
        }
      } catch (error) {
        // El interceptor de api maneja 401 y redirige a login
      } finally {
        if (mounted) setHydratingUser(false)
      }
    }

    hydrateUser()
    return () => {
      mounted = false
    }
  }, [token, user, setUser])

  useEffect(() => {
    let mounted = true
    const path = location.pathname

    async function hydrateCombat() {
      if (!token || !user) {
        setActiveCombat(null)
        setCombatCheckPath(path)
        setCombatChecked(true)
        return
      }

      setActiveCombat(null)
      setCombatChecked(false)
      try {
        const res = await api.get(`/combat/active?t=${Date.now()}`)
        if (mounted) {
          setActiveCombat(res.data?.combat || null)
        }
      } catch (_error) {
        if (mounted) {
          setActiveCombat(null)
        }
      } finally {
        if (mounted) {
          setCombatCheckPath(path)
          setCombatChecked(true)
        }
      }
    }

    hydrateCombat()
    return () => {
      mounted = false
    }
  }, [token, user, location.pathname])

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />
  }

  if (hydratingUser && !user) {
    return (
      <div className="min-h-screen bg-[#04060d] text-cyan-200 flex items-center justify-center">
        Restaurando sesion...
      </div>
    )
  }

  if (user && !isCombatCheckCurrent && !hasCombatNavigationState) {
    return (
      <div className="min-h-screen bg-[#04060d] text-cyan-200 flex items-center justify-center">
        Restaurando combate...
      </div>
    )
  }

  if (activeCombat && location.pathname !== '/combat') {
    return <Navigate to="/combat" replace />
  }

  if (!activeCombat && location.pathname === '/combat' && isCombatCheckCurrent && !hasCombatNavigationState) {
    return <Navigate to="/game" replace />
  }

  if (requireAdmin && !user?.isAdmin) {
    return <Navigate to="/dashboard" replace />
  }

  return children ? children : <Outlet />
}
